import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { GoogleMap, Marker, InfoWindow, useJsApiLoader } from '@react-google-maps/api';
import { Box, GlobalStyles, IconButton, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useTranslation } from 'react-i18next';
import CourierService from '../../services/courier/courierService.ts';
import { CircularProgress } from '@mui/material';
import { useNotify } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';

/**
 * One courier on the map. VELLO couriers report from their own phone; WOLT
 * couriers are positioned from Wolt's webhooks and have no Vello id, so the
 * order they carry identifies them.
 */
type MapCourier = {
    courierId: number | null;
    courierName: string;
    vehicleType: string | null;
    latitude: number;
    longitude: number;
    timestamp: string | null;
    source?: 'VELLO' | 'WOLT';
    orderId?: number | null;
    orderNumber?: string | null;
    restaurantName?: string | null;
    deliveryState?: string | null;
};

const VELLO_COLOR = '#24bbaa';
const WOLT_COLOR = '#009de0';

const containerStyle = {
    flex: 6,
    height: '75vh',
};

// Used only until the first positions arrive; the map then fits to them.
const center = {
    lat: 34.6930,
    lng: 33.0220,
};

const isWolt = (courier: MapCourier) => courier.source === 'WOLT';
const keyOf = (courier: MapCourier) =>
    isWolt(courier) ? `wolt-${courier.orderId}` : `vello-${courier.courierId}`;
const colorOf = (courier: MapCourier) => (isWolt(courier) ? WOLT_COLOR : VELLO_COLOR);

/** The server writes UTC without a zone; read it as UTC, not as the browser's time. */
const minutesSince = (timestamp: string | null) => {
    if (!timestamp) return null;
    const iso = /[zZ]|[+-]\d\d:?\d\d$/.test(timestamp) ? timestamp : `${timestamp}Z`;
    const at = Date.parse(iso);
    return Number.isNaN(at) ? null : Math.max(0, Math.round((Date.now() - at) / 60000));
};

export const OrdersMapScreen = () => {
    const [selectedCourier, setSelectedCourier] = useState<MapCourier | null>(null);
    const { t } = useTranslation();
    const notify = useNotify();
    const navigate = useNavigate();
    const courierService = new CourierService();
    const mapRef = useRef<google.maps.Map | null>(null);
    const fitted = useRef(false);

    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_PLACES_API_KEY!,
        libraries: ['places'],
    });

    const {
        data: couriers = [],
    } = useQuery<MapCourier[]>({
        queryKey: ['couriers'],
        queryFn: async () => {
            try {
                return await courierService.getLastLocation();
            } catch (error) {
                console.error('getLastLocation error:', error);
                notify(error?.response?.data?.error || 'Internal error', { type: 'error' });
                Sentry.captureException(error);
                return [];
            }
        },
        // Wolt reports a moving courier every few seconds; two minutes left
        // the pin a kilometre behind.
        refetchInterval: 20 * 1000,
        refetchIntervalInBackground: true,
    });

    const velloCouriers = couriers.filter((c) => !isWolt(c));
    const woltCouriers = couriers.filter(isWolt);

    // Couriers can be in Limassol, Budapest or Athens; open on wherever they
    // are, once, and leave the zoom alone after that.
    useEffect(() => {
        if (fitted.current || !isLoaded || !mapRef.current || couriers.length === 0) return;
        if (couriers.length === 1) {
            mapRef.current.setCenter({ lat: Number(couriers[0].latitude), lng: Number(couriers[0].longitude) });
            mapRef.current.setZoom(14);
        } else {
            const bounds = new window.google.maps.LatLngBounds();
            couriers.forEach((c) => bounds.extend({ lat: Number(c.latitude), lng: Number(c.longitude) }));
            mapRef.current.fitBounds(bounds, 64);
        }
        fitted.current = true;
    }, [couriers, isLoaded]);

    const ago = (courier: MapCourier) => {
        const minutes = minutesSince(courier.timestamp);
        if (minutes == null) return null;
        const when = minutes < 1
            ? t('orders.map.justNow')
            : minutes < 60
                ? t('orders.map.minutesAgo', { count: minutes })
                : t('orders.map.hoursAgo', { count: Math.floor(minutes / 60) });
        return t('orders.map.updated', { ago: when });
    };

    const focus = (courier: MapCourier) => {
        setSelectedCourier(courier);
        if (mapRef.current) {
            mapRef.current.setZoom(15);
            mapRef.current.panTo({ lat: Number(courier.latitude), lng: Number(courier.longitude) });
        }
    };

    const sectionTitle = (label: string, color: string, count: number) => (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 16px',
                backgroundColor: '#fafafa',
                borderBottom: '1px solid #f0f0f0',
                fontSize: '12px',
                fontWeight: 700,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: '#555',
            }}
        >
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: color, flexShrink: 0 }} />
            {label} · {count}
        </Box>
    );

    const row = (courier: MapCourier) => (
        <Box
            key={keyOf(courier)}
            sx={{
                padding: '12px 16px',
                borderBottom: '1px solid #f0f0f0',
                fontSize: '14px',
                color: '#111',
                cursor: 'pointer',
                backgroundColor: selectedCourier && keyOf(selectedCourier) === keyOf(courier) ? '#f2f8fb' : 'transparent',
                '&:hover': { backgroundColor: '#f7f7f7' },
            }}
            onClick={() => focus(courier)}
        >
            <Box sx={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {isWolt(courier) ? `${courier.orderNumber} · ${courier.restaurantName ?? ''}` : courier.courierName}
            </Box>
            <Box sx={{ fontSize: '12px', color: '#777', marginTop: '2px' }}>
                {[
                    isWolt(courier) && courier.deliveryState ? t(`orders.wolt.${courier.deliveryState}`) : null,
                    ago(courier),
                ].filter(Boolean).join(' · ')}
            </Box>
        </Box>
    );

    const empty = (text: string) => (
        <Box sx={{ padding: '12px 16px', fontSize: '13px', color: '#888', borderBottom: '1px solid #f0f0f0' }}>
            {text}
        </Box>
    );

    if (!isLoaded) {
        return (
            <Box
                sx={{
                    height: '75vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <>
            <GlobalStyles
                styles={{
                    '.gm-style .gm-style-iw': {
                        background: 'transparent !important',
                        boxShadow: 'none !important',
                        border: 'none !important',
                        padding: '0 !important',
                        overflow: 'visible !important',
                    },

                    '.gm-style .gm-style-iw-t::after, .gm-style-iw-chr, .gm-style-iw-tc': {
                        display: 'none !important'
                    },

                    '.gm-style-iw-d': {
                        overflow: 'auto !important'
                    },

                    '.courier-tooltip': {
                        color: '#ffffff',
                        fontWeight: 600,
                        fontSize: '13px',
                        padding: '4px 10px',
                        borderRadius: '999px',
                        whiteSpace: 'nowrap',
                        pointerEvents: 'none'
                    }
                }}
            />
            <Box sx={{ px: 'var(--space-32)' }}>
                <Box
                    sx={{
                        paddingTop: 'var(--space-72)',
                        paddingBottom: 'var(--space-32)',
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton onClick={() => navigate(-1)} sx={{ color: 'text.primary' }}>
                            <ArrowBackIcon sx={{ fontSize: '2rem' }} />
                        </IconButton>
                        <Typography
                            variant="h5"
                            sx={{ paddingLeft: 'var(--space-24)', fontWeight: 700, fontSize: 'var(--text-xxl)' }}
                        >
                            {t('resources.orders')}
                        </Typography>
                    </Box>
                </Box>
                <Box style={{ display: "flex", height: 'fit-content' }}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            flex: 1.4,
                            minWidth: 0,
                            height: '75vh',
                            overflowY: 'auto',
                            border: '1px solid #f0f0f0',
                        }}
                    >
                        {sectionTitle(t('orders.map.velloCouriers'), VELLO_COLOR, velloCouriers.length)}
                        {velloCouriers.length ? velloCouriers.map(row) : empty(t('orders.map.noVelloCouriers'))}
                        {sectionTitle(t('orders.map.woltCouriers'), WOLT_COLOR, woltCouriers.length)}
                        {woltCouriers.length ? woltCouriers.map(row) : empty(t('orders.map.noWoltCouriers'))}
                    </Box>

                    <GoogleMap
                        mapContainerStyle={containerStyle}
                        center={center}
                        zoom={13}
                        onLoad={(map) => {
                            mapRef.current = map;
                        }}
                        options={{
                            disableDefaultUI: true,
                            zoomControl: true,
                            styles: [
                                {
                                    featureType: 'all',
                                    stylers: [{ saturation: -100 }],
                                },
                            ],
                        }}
                    >
                        {couriers.map((courier) => (
                            <Marker
                                key={keyOf(courier)}
                                position={{
                                    lat: Number(courier.latitude),
                                    lng: Number(courier.longitude),
                                }}
                                icon={{
                                    path: window.google.maps.SymbolPath.CIRCLE,
                                    scale: 8,
                                    fillColor: colorOf(courier),
                                    fillOpacity: 1,
                                    strokeColor: isWolt(courier) ? '#ffffff' : '#000000',
                                    strokeWeight: 2,
                                }}
                                onClick={() => focus(courier)}
                                onMouseOver={() => setSelectedCourier(courier)}
                                onMouseOut={() => setSelectedCourier(null)}
                            />
                        ))}

                        {selectedCourier && (
                            <InfoWindow
                                position={{
                                    lat: Number(selectedCourier.latitude),
                                    lng: Number(selectedCourier.longitude),
                                }}
                                options={{
                                    pixelOffset: new window.google.maps.Size(0, -5),
                                    disableAutoPan: true,
                                }}
                            >
                                <div className="courier-tooltip" style={{ background: colorOf(selectedCourier) }}>
                                    {isWolt(selectedCourier)
                                        ? `${t('orders.map.legendWolt')} · ${selectedCourier.orderNumber}`
                                        : selectedCourier.courierName}
                                </div>
                            </InfoWindow>
                        )}
                    </GoogleMap>
                </Box>
            </Box>
        </>
    );
};
